/* eslint-disable */
import React from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';

// Contextual icons per metric category
import StorefrontIcon from '@mui/icons-material/Storefront';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DateRangeIcon from '@mui/icons-material/DateRange';
import BarChartIcon from '@mui/icons-material/BarChart';
import SpeakerIcon from '@mui/icons-material/Speaker';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CancelIcon from '@mui/icons-material/Cancel';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
// Nav button icons
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';

import urbanTheme from 'views/urban-dashboard/theme/urbanTheme';
import { useUrbanDashboardData } from 'hooks/useUrbanDashboardData';
import SkeletonGrid from 'views/urban-dashboard/components/SkeletonGrid';
import ErrorState from 'views/urban-dashboard/components/ErrorState';
import EmptyState from 'views/urban-dashboard/components/EmptyState';
import MetricCard from 'views/urban-dashboard/components/MetricCard';

/**
 * Maps each metric field name to a contextual MUI icon.
 */
const METRIC_ICONS = {
    active_merchants: <StorefrontIcon />,
    total_merchants: <StorefrontIcon />,
    top_merchants: <TrendingUpIcon />,
    pending_merchants: <PendingActionsIcon />,
    rejected_merchants: <CancelIcon />,
    active_branches: <AccountTreeIcon />,
    active_sound_box: <SpeakerIcon />,
    total_txn_last_day_count: <CalendarTodayIcon />,
    total_txn_last_day_volume: <CalendarTodayIcon />,
    total_txn_current_week_count: <DateRangeIcon />,
    total_txn_current_week_volume: <DateRangeIcon />,
    total_txn_current_month_count: <DateRangeIcon />,
    total_txn_current_month_volume: <DateRangeIcon />,
    total_txn_count: <BarChartIcon />,
    total_txn_volume: <AccountBalanceWalletIcon />
};

/**
 * Returns the appropriate icon for a given metric field name.
 */
function getMetricIcon(fieldName) {
    return METRIC_ICONS[fieldName] || <BarChartIcon />;
}

/**
 * UrbanDashboard — Premium metrics dashboard with glassmorphic card design,
 * contextual metric icons, and modern page composition.
 *
 * Props API preserved: role
 * Behavior preserved: state machine (loading | error | empty | loaded)
 */
function UrbanDashboard({ role }) {
    const { state, metrics, error, retry } = useUrbanDashboardData(role);
    const navigate = useNavigate();

    // Navigation links based on role
    const navItems =
        role === 'super-distributor'
            ? [
                  { label: 'Top Branches', icon: <AccountTreeOutlinedIcon />, path: '/super-distributor/urban-top-branches' },
                  { label: 'Merchants List', icon: <PeopleAltOutlinedIcon />, path: '/super-distributor/urban-merchants' },
                  { label: 'Transactions List', icon: <ReceiptLongOutlinedIcon />, path: '/super-distributor/urban-transactions' }
              ]
            : [
                  { label: 'Merchants List', icon: <PeopleAltOutlinedIcon />, path: '/distributor/urban-merchants' },
                  { label: 'Transactions List', icon: <ReceiptLongOutlinedIcon />, path: '/distributor/urban-transactions' }
              ];

    const merchantFields = [
        'active_merchants',
        'total_merchants',
        'pending_merchants',
        'rejected_merchants',
        'top_merchants',
        'active_branches',
        'active_sound_box'
    ];

    const merchantMetrics = metrics ? metrics.filter((m) => merchantFields.includes(m.fieldName)) : [];
    const transactionMetrics = metrics ? metrics.filter((m) => !merchantFields.includes(m.fieldName)) : [];

    const renderContent = () => {
        switch (state) {
            case 'loading':
                return <SkeletonGrid />;

            case 'error':
                return <ErrorState errorCategory={error?.category || 'server'} onRetry={retry} />;

            case 'empty':
                return <EmptyState />;

            case 'loaded':
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 4, md: 5 } }}>
                        {merchantMetrics.length > 0 && (
                            <Box>
                                <Typography
                                    variant="h6"
                                    component="h2"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        color: 'text.secondary',
                                        mb: 2,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        userSelect: 'none'
                                    }}
                                >
                                    Merchant Network
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'primary.main' }} />
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: 'repeat(2, 1fr)',
                                            md: 'repeat(3, 1fr)',
                                            lg: 'repeat(4, 1fr)'
                                        },
                                        gap: { xs: '16px', md: '20px' },
                                        width: '100%'
                                    }}
                                >
                                    {merchantMetrics.map((metric, index) => (
                                        <MetricCard
                                            key={metric.fieldName}
                                            label={metric.label}
                                            value={metric.value}
                                            isVolume={metric.isVolume}
                                            icon={getMetricIcon(metric.fieldName)}
                                            animationDelay={index * 0.03}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {transactionMetrics.length > 0 && (
                            <Box>
                                <Typography
                                    variant="h6"
                                    component="h2"
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: '13px',
                                        color: 'text.secondary',
                                        mb: 2,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.06em',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        userSelect: 'none'
                                    }}
                                >
                                    Transaction Activity
                                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'success.main' }} />
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: 'repeat(2, 1fr)',
                                            md: 'repeat(3, 1fr)',
                                            lg: 'repeat(4, 1fr)'
                                        },
                                        gap: { xs: '16px', md: '20px' },
                                        width: '100%'
                                    }}
                                >
                                    {transactionMetrics.map((metric, index) => (
                                        <MetricCard
                                            key={metric.fieldName}
                                            label={metric.label}
                                            value={metric.value}
                                            isVolume={metric.isVolume}
                                            icon={getMetricIcon(metric.fieldName)}
                                            animationDelay={(merchantMetrics.length + index) * 0.03}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                );

            default:
                return null;
        }
    };

    const getStateAnnouncement = () => {
        switch (state) {
            case 'loading':
                return 'Loading dashboard metrics';
            case 'error':
                return 'Error loading dashboard metrics';
            case 'empty':
                return 'No transaction data available';
            case 'loaded':
                return `Dashboard loaded with ${metrics ? metrics.length : 0} metrics`;
            default:
                return '';
        }
    };

    return (
        <ThemeProvider theme={urbanTheme}>
            {/* Full page background with subtle radial gradient */}
            <Box
                sx={{
                    minHeight: '100vh',
                    background: 'linear-gradient(160deg, #F8FAFC 0%, #EEF2FF 35%, #F0FDF4 70%, #F8FAFC 100%)',
                    position: 'relative',
                    overflow: 'hidden',
                    // Decorative blurred orbs
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '-120px',
                        right: '-80px',
                        width: '400px',
                        height: '400px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.06) 0%, transparent 70%)',
                        pointerEvents: 'none'
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: '-100px',
                        left: '-60px',
                        width: '350px',
                        height: '350px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.05) 0%, transparent 70%)',
                        pointerEvents: 'none'
                    }
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        py: { xs: 3.5, sm: 5, md: 6 },
                        px: { xs: 2, sm: 3.5, md: 5 }
                    }}
                >
                    <main style={{ maxWidth: '1400px', margin: '0 auto' }}>
                        <section aria-labelledby="urban-dashboard-heading">
                            {/* Header block — title left, nav buttons right */}
                            <Box
                                sx={{
                                    mb: { xs: 3.5, md: 5 },
                                    display: 'flex',
                                    flexDirection: { xs: 'column', md: 'row' },
                                    alignItems: { xs: 'flex-start', md: 'center' },
                                    justifyContent: 'space-between',
                                    gap: { xs: 2.5, md: 3 }
                                }}
                            >
                                {/* Left: title + subtitle */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {/* Status pill */}
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: '100px',
                                            background: 'rgba(16, 185, 129, 0.08)',
                                            border: '1px solid rgba(16, 185, 129, 0.12)',
                                            mb: 1.5
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 7,
                                                height: 7,
                                                borderRadius: '50%',
                                                background: 'linear-gradient(135deg, #10B981, #059669)',
                                                animation: 'pulse-glow 2s ease-in-out infinite',
                                                '@keyframes pulse-glow': {
                                                    '0%, 100%': { opacity: 1, boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.4)' },
                                                    '50%': { opacity: 0.7, boxShadow: '0 0 0 4px rgba(16, 185, 129, 0)' }
                                                }
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontSize: '11px',
                                                fontWeight: 700,
                                                color: '#059669',
                                                letterSpacing: '0.06em',
                                                textTransform: 'uppercase',
                                                lineHeight: 1
                                            }}
                                        >
                                            Real-time
                                        </Typography>
                                    </Box>

                                    <Typography
                                        id="urban-dashboard-heading"
                                        variant="h4"
                                        component="h1"
                                        sx={{
                                            fontSize: { xs: '26px', sm: '32px', md: '36px' },
                                            fontWeight: 800,
                                            letterSpacing: '-0.035em',
                                            color: '#0F172A',
                                            mb: 0.75,
                                            lineHeight: 1.15
                                        }}
                                    >
                                        Urban Dashboard
                                    </Typography>

                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#64748B',
                                            fontSize: { xs: '14px', sm: '15px' },
                                            maxWidth: '540px',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        Monitor merchant activity, transaction volumes, and business performance across your urban network.
                                    </Typography>
                                </Box>

                                {/* Right: navigation buttons */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 1.25,
                                        flexShrink: 0
                                    }}
                                >
                                    {navItems.map((item) => (
                                        <Box
                                            key={item.label}
                                            component="button"
                                            onClick={() => navigate(item.path)}
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                px: 2,
                                                py: 1,
                                                border: '1px solid rgba(15, 23, 42, 0.08)',
                                                borderRadius: '10px',
                                                background: 'rgba(255, 255, 255, 0.65)',
                                                backdropFilter: 'blur(8px)',
                                                WebkitBackdropFilter: 'blur(8px)',
                                                cursor: 'pointer',
                                                transition: 'all 200ms cubic-bezier(0.22, 1, 0.36, 1)',
                                                outline: 'none',
                                                fontFamily: 'inherit',
                                                '&:hover': {
                                                    background: 'rgba(255, 255, 255, 0.9)',
                                                    borderColor: 'rgba(99, 102, 241, 0.3)',
                                                    boxShadow: '0 2px 8px -2px rgba(99, 102, 241, 0.12)',
                                                    transform: 'translateY(-1px)',
                                                    '& .nav-arrow': {
                                                        transform: 'translateX(2px)',
                                                        opacity: 1
                                                    }
                                                },
                                                '&:focus-visible': {
                                                    outline: '2px solid #4F46E5',
                                                    outlineOffset: '2px'
                                                },
                                                '&:active': {
                                                    transform: 'translateY(0)',
                                                    boxShadow: 'none'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', color: '#4F46E5' }}>
                                                {React.cloneElement(item.icon, { sx: { fontSize: '16px' } })}
                                            </Box>
                                            <Typography
                                                component="span"
                                                sx={{
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    color: '#1E293B',
                                                    letterSpacing: '-0.01em',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {item.label}
                                            </Typography>
                                            <ArrowForwardIcon
                                                className="nav-arrow"
                                                sx={{
                                                    fontSize: '14px',
                                                    color: '#94A3B8',
                                                    opacity: 0.5,
                                                    transition: 'all 200ms ease',
                                                    ml: 0.25
                                                }}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>

                            {/* ARIA live region */}
                            <div aria-live="polite" aria-atomic="true">
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        width: '1px',
                                        height: '1px',
                                        padding: 0,
                                        margin: '-1px',
                                        overflow: 'hidden',
                                        clip: 'rect(0, 0, 0, 0)',
                                        whiteSpace: 'nowrap',
                                        borderWidth: 0
                                    }}
                                >
                                    {getStateAnnouncement()}
                                </Box>

                                {renderContent()}
                            </div>
                        </section>
                    </main>
                </Box>
            </Box>
        </ThemeProvider>
    );
}

UrbanDashboard.propTypes = {
    role: PropTypes.oneOf(['distributor', 'super-distributor']).isRequired
};

export default UrbanDashboard;
